import React, { useEffect, useMemo, useState } from "react";
import "./WealthSeriesPortfolioPerformanceGraph.scss";
import SparkLines from "../../../common/SparkLines";
import { dummyOptions } from "../../../TradingView/helpers";
import { connect } from "react-redux";
import {
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { portfolioPerformanceLoad } from "../../../../appRedux/ducks/wealthPerformance";
import { multiSparklineDataLoad } from "../../../../appRedux/ducks/multiSparklineData";
import moment from "moment";
import { useParams } from "react-router-dom";
import { init_portfolio } from "../../../../appRedux/ducks/wealthPortfolio";
import { urlToNameMap } from "../../../WealthSeriesOverview/portfoliosData";
import { apiURL } from "../../../../appRedux/actions/helpers";

const WealthSeriesPortfolioPerformanceGraph = ({
  loadSparkline,
  sparklineLoading,
  sparklineData,
  wealthPortfolios,
  loadingPortfolio,
  tabValue,
  loadSpyData,
  spyData,
  spyLoading,
  spyTimeframe,
  timeframe,
  setTimeframe,
  portfolioData,
  simulatedCutoff,
}) => {
  const { portfolio } = useParams();
  const { series: _series } = useParams();
  const [resolution, setResolution] = useState(timeframe || "1y");
  const [showSPY, setShowSPY] = useState(true);
  const [wowMomPerformance, setWowMomPerformance] = useState(null);

  useEffect(() => {
    if (timeframe !== resolution) {
      setResolution(timeframe);
    }
  }, [timeframe]);

  const handleResolutionChange = (_, newResolution) => {
    if (newResolution) {
      setResolution(newResolution);
      if (setTimeframe) {
        setTimeframe(newResolution);
      }
    }
  };
  const handleSPYToggle = (event) => {
    setShowSPY(event.target.checked);
  };

  // Use portfolioData from props if available, otherwise fall back to wealthPortfolios[portfolio]
  const wealthPortfolio =
    portfolioData || wealthPortfolios[portfolio] || init_portfolio;

  const scalingFactor = 100; // Adjust this factor as needed

  const memoizedSparklineData = useMemo(() => {
    return (
      sparklineData?.map((el, i) => {
        const timestamp = moment(el.created_at).valueOf();
        return {
          x: i,
          date: timestamp,
          y:
            tabValue === 0
              ? (Number(el.twr_performance) * scalingFactor).toFixed(2)
              : (Number(el.total_growth) * scalingFactor).toFixed(2),
        };
      }) || []
    );
  }, [sparklineData, tabValue]);

  // Calculate overall performance for the current timeframe
  const overallPerformance = useMemo(() => {
    if (sparklineData?.length > 0) {
      const lastPoint = sparklineData[sparklineData.length - 1];
      const performance =
        tabValue === 0
          ? Number(lastPoint.twr_performance) * scalingFactor
          : Number(lastPoint.total_growth) * scalingFactor;
      return performance.toFixed(2);
    }
    return "0.00";
  }, [sparklineData, tabValue, scalingFactor]);

  // Get timeframe display text
  const timeframeText = useMemo(() => {
    if (sparklineData?.length > 0) {
      const startDate = moment(sparklineData[0].created_at).format("MMM YYYY");
      return `${startDate} – Today`;
    }
    return "";
  }, [sparklineData]);

  // Format date labels based on timeframe
  const dateLabels = useMemo(() => {
    if (!sparklineData?.length) return { startDate: "", endDate: "" };

    const isShortTimeframe =
      resolution === "1d" || resolution === "7d" || resolution === "1m";
    const format = isShortTimeframe ? "MMM DD, YYYY" : "MMM YYYY";

    const startDate = moment(sparklineData[0].created_at).format(format);
    const endDate = moment(
      sparklineData[sparklineData.length - 1].created_at,
    ).format(format);

    return { startDate, endDate };
  }, [sparklineData, resolution]);

  const updatedOptions = useMemo(() => {
    if (sparklineData?.length) {
      const isUpwardTrend =
        sparklineData.length > 1 &&
        sparklineData[0].twr_performance <
          sparklineData[sparklineData.length - 1].twr_performance;
      // simulatedCutoff overrides the per-series default; null disables the
      // "Simulated data" band entirely (e.g. Bitcoin Navigator is live-only).
      const cutoffDateMs =
        simulatedCutoff !== undefined
          ? simulatedCutoff === null
            ? null
            : moment(simulatedCutoff).valueOf()
          : _series === "core"
            ? moment("2025-09-25").valueOf()
            : moment("2024-12-23").valueOf();
      let cutoffIndex =
        cutoffDateMs === null
          ? 0
          : memoizedSparklineData.findIndex(
              (point) => point.date > cutoffDateMs,
            );
      cutoffIndex =
        cutoffIndex === -1 ? memoizedSparklineData.length - 1 : cutoffIndex - 1;
      const annotations = {
        xaxis:
          cutoffIndex > 0
            ? [
                {
                  x: 0,
                  x2: memoizedSparklineData[cutoffIndex].x,
                  fillColor: "#ECECEC",
                  opacity: 0.4,
                  label: {
                    borderColor: "#ECECEC",
                    style: {
                      color: "#333",
                      background: "#ECECEC",
                    },
                    text: "Simulated data",
                  },
                },
              ]
            : [],
      };

      // Setup series data
      const series = [
        {
          name: "Portfolio",
          data: memoizedSparklineData,
        },
      ];

      // Add SPY data as second series if enabled and timeframe matches
      if (
        showSPY &&
        spyData?.length > 0 &&
        memoizedSparklineData.length > 0 &&
        spyTimeframe === resolution.toUpperCase()
      ) {
        // Ensure SPY data has same length as portfolio data for proper alignment
        const spyNormalizedData = [];

        // First SPY value becomes starting point (0% change)
        const firstSpyValue = parseFloat(spyData[0]);

        if (!isNaN(firstSpyValue) && firstSpyValue > 0) {
          // Map each portfolio data point to corresponding SPY data point
          for (let i = 0; i < memoizedSparklineData.length; i++) {
            // Calculate the corresponding SPY index based on ratio
            const spyIndex = Math.min(
              Math.floor(i * (spyData.length / memoizedSparklineData.length)),
              spyData.length - 1,
            );

            const currentSpyValue = parseFloat(spyData[spyIndex]);

            if (!isNaN(currentSpyValue) && currentSpyValue > 0) {
              const percentChange =
                ((currentSpyValue - firstSpyValue) / firstSpyValue) * 100;

              spyNormalizedData.push({
                x: i,
                y: parseFloat(percentChange.toFixed(2)),
                // Copy date for tooltip alignment
                date: memoizedSparklineData[i]?.date,
              });
            }
          }

          // Only add SPY series if we have valid data
          if (spyNormalizedData.length > 0) {
            series.push({
              name: "S&P 500",
              data: spyNormalizedData,
            });
          }
        }
      }

      return {
        ...dummyOptions,
        tooltip: {
          ...dummyOptions.tooltip,
          shared: true,
          x: {
            formatter: (_, { dataPointIndex }) => {
              const date = sparklineData[dataPointIndex]?.created_at;
              return date
                ? moment.tz(date, "America/New_York").format("MMM DD, YYYY")
                : "";
            },
          },
          y: {
            formatter: (val, { seriesIndex, dataPointIndex }) => {
              if (seriesIndex === 0) {
                const currentValue =
                  (
                    sparklineData[dataPointIndex]?.twr_performance * 100
                  ).toFixed(2) + "%";
                return `${currentValue}`;
              } else {
                return `${val}%`;
              }
            },
          },
        },
        colors: [
          tabValue === 1 ? "#1976d2" : isUpwardTrend ? "#5EEECD" : "#F32228",
          "#1976d2", // S&P 500 color
        ],
        stroke: {
          width: [2, 2],
          curve: "smooth",
        },
        annotations,
        series,
      };
    }
    return dummyOptions;
  }, [
    sparklineData,
    tabValue,
    memoizedSparklineData,
    showSPY,
    spyData,
    spyTimeframe,
    resolution,
    _series,
    simulatedCutoff,
  ]);

  const renderInfo = (resolution, performances) => {
    if (resolution !== "1d" && resolution !== "1y" && resolution !== "ytd") {
      const label =
        resolution === "7d" ? "Week-on-week (WoW): " : "Month-on-month (MoM): ";

      // Use dedicated WoW/MoM endpoint data if available
      if (
        (resolution === "7d" || resolution === "1m") &&
        wowMomPerformance !== null
      ) {
        const displayValue = Number((wowMomPerformance * 100).toFixed(2));
        return label + (displayValue >= 0 ? "+" : "") + displayValue + "%";
      }

      // Fallback to original logic
      const performance = performances?.find(
        (data) => data.period === resolution,
      );
      const performanceValue = performance?.performance;
      const displayValue = Number((performanceValue * 100).toFixed(2));
      return label + displayValue + "%";
    }
    return null;
  };

  useEffect(() => {
    if (wealthPortfolio.portfolio_id && resolution) {
      loadSparkline(wealthPortfolio.portfolio_id, resolution);
    }
  }, [resolution, wealthPortfolio.portfolio_id, loadSparkline]);

  useEffect(() => {
    if (showSPY) {
      loadSpyData(["SPY"], "1", true, resolution.toUpperCase());
    }
  }, [showSPY, loadSpyData, resolution]);

  // Fetch WoW/MoM data from dedicated endpoints
  useEffect(() => {
    if (
      wealthPortfolio.portfolio_id &&
      (resolution === "7d" || resolution === "1m")
    ) {
      const periodType = resolution === "7d" ? "wow" : "mom";
      const fetchWowMomData = async () => {
        try {
          const response = await fetch(
            `${apiURL}/wealth-series/portfolios/${wealthPortfolio.portfolio_id}/performance?period=${periodType}`,
            { credentials: "include" },
          );
          const data = await response.json();

          if (data.performance?.records?.[0]?.twr_performance !== undefined) {
            setWowMomPerformance(data.performance.records[0].twr_performance);
          } else {
            setWowMomPerformance(null);
          }
        } catch (error) {
          console.error("Failed to fetch WoW/MoM data:", error);
          setWowMomPerformance(null);
        }
      };

      fetchWowMomData();
    } else {
      setWowMomPerformance(null);
    }
  }, [resolution, wealthPortfolio.portfolio_id]);

  const isPositivePerformance = parseFloat(overallPerformance) >= 0;

  return (
    <>
      {/* Performance header with S&P500 toggle */}
      <div className="ws-portfolio-performance-header">
        <div className="ws-portfolio-performance-summary">
          <div className="ws-portfolio-performance-value">
            <span className={isPositivePerformance ? "positive" : "negative"}>
              {isPositivePerformance && "+"}
              {overallPerformance}%
            </span>
          </div>
          <div className="ws-portfolio-performance-timeframe">
            {timeframeText}
          </div>
        </div>

        <div className="ws-portfolio-spy-toggle">
          <FormControlLabel
            control={
              <Switch
                checked={showSPY}
                onChange={handleSPYToggle}
                color="primary"
              />
            }
            labelPlacement="start"
            label={
              <div className="spy-toggle-label">
                <span
                  className={`spy-color-dot ${showSPY ? "active" : ""}`}
                ></span>
                <span>S&P500</span>
              </div>
            }
          />
        </div>
      </div>

      <div className="ws-portfolio-performance-graph">
        {sparklineLoading || (showSPY && spyLoading) ? (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <SparkLines
              series={
                updatedOptions.series || [
                  {
                    name: urlToNameMap[portfolio],
                    data: memoizedSparklineData,
                  },
                ]
              }
              options={updatedOptions}
              type={updatedOptions.chart.type}
              height={updatedOptions.chart.height}
              width={updatedOptions.chart.width}
              className="w-full"
            />

            {/* Date labels */}
            <div className="ws-portfolio-chart-date-labels">
              <span className="start-date">{dateLabels.startDate}</span>
              <span className="end-date">{dateLabels.endDate}</span>
            </div>
          </>
        )}

        <div className="ws-portfolio-performance-graph-inform">
          {renderInfo(resolution, wealthPortfolio?.performance)}
        </div>
        {_series === "core" && (
          <div className="ws-portfolio-performance-graph-inform mb-0">
            Data before Sep 25, 2025 is simulated
          </div>
        )}
        <ToggleButtonGroup
          value={resolution}
          exclusive
          onChange={handleResolutionChange}
          aria-label="Graph Period"
          className="ws-portfolio-resolutions ws-portfolio-performance-toggles"
          disabled={loadingPortfolio}
        >
          {(loadingPortfolio
            ? [
                { period: "1d" },
                { period: "7d" },
                { period: "1m" },
                { period: "1y" },
                { period: "ytd" },
              ]
            : wealthPortfolio?.performance || []
          )
            ?.filter(
              (data) =>
                data?.period?.toUpperCase() !== "ITD" &&
                data?.period?.toUpperCase() !== "1H" &&
                ["1D", "7D", "1M", "1Y", "YTD"].includes(
                  data?.period?.toUpperCase(),
                ),
            )
            ?.map((data, index) => {
              const periodValue = data?.period?.toLowerCase();
              const performanceValue = data?.performance;
              const displayValue = Number((performanceValue * 100).toFixed(2));
              const isPositive = displayValue >= 0;

              return (
                <ToggleButton
                  key={"performance_toggle_" + (periodValue || index)}
                  value={periodValue}
                  className="ws-portfolio-resolution ws-portfolio-info-performance-toggle"
                  disabled={loadingPortfolio}
                >
                  {loadingPortfolio ? (
                    <>
                      <div className="ws-portfolio-info-performance-label">
                        {data.period.toUpperCase()}
                      </div>
                      <div className="ws-portfolio-info-performance-value">
                        --.--%
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ws-portfolio-info-performance-label">
                        {data?.period?.toUpperCase()}
                      </div>
                      <div
                        className={`ws-portfolio-info-performance-value ${isPositive ? "positive" : "negative"}`}
                      >
                        {isPositive && "+"}
                        {displayValue === 0 ? "0.00" : displayValue.toFixed(2)}%
                      </div>
                    </>
                  )}
                </ToggleButton>
              );
            })}
        </ToggleButtonGroup>
      </div>
    </>
  );
};

const stateToProps = (state) => ({
  sparklineData: state.wealthPerformance.sparklineData,
  sparklineLoading: state.wealthPerformance.loading,
  wealthPortfolios: state.wealthPortfolio,
  spyData: state.multiSparklineData.sparklineData.SPY || [],
  spyLoading: state.multiSparklineData.loading.SPY || false,
  spyTimeframe: state.multiSparklineData.timeframes?.SPY || null,
});

const dispatchToProps = (dispatch) => ({
  loadSparkline: (portfolioId, resolution) =>
    dispatch(portfolioPerformanceLoad(portfolioId, resolution)),
  loadSpyData: (symbols, resolution, forceRefresh, timeframe) =>
    dispatch(
      multiSparklineDataLoad(
        symbols,
        resolution,
        forceRefresh,
        timeframe || resolution.toUpperCase(),
      ),
    ),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioPerformanceGraph);
